import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';

export default function BlogPeoplePleasingCoping() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-white to-warm-50">
      <Navigation variant="soft-brown" scrolled={true} />
      
      {/* Article Header */}
      <section className="px-4 sm:px-6 lg:px-8 pt-32 pb-12">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog" className="inline-flex items-center text-sage-600 hover:text-sage-800 mb-8 transition-colors">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Blog
          </Link>
          
          <div className="mb-6">
            <span className="inline-block px-3 py-1 bg-warm-100 text-warm-700 text-sm font-medium rounded-full">
              Understanding People-Pleasing
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-sage-800 mb-6">
            People-Pleasing Is a Brilliant Coping Mechanism
          </h1>
          
          <div className="flex items-center text-gray-600 text-sm mb-8">
            <span>By Yasi Philipos</span>
            <span className="mx-3">•</span>
            <time>November 2024</time>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
            
            {/* Introduction */}
            <div className="bg-gradient-to-r from-sage-50 to-warm-50 pl-8 border-l-4 border-sage-400 py-6 rounded-r-2xl mb-10">
              <p className="text-lg text-gray-700 leading-relaxed mb-0">
                <strong className="text-sage-700">People-pleasing is a brilliant coping mechanism.</strong>
              </p>
            </div>

            {/* Main Content */}
            <div className="space-y-8">
              <p className="text-xl text-gray-700 leading-relaxed">
                When emotional attunement was missing, you adapted by becoming hyper-attuned to others. You learned to read the room, anticipate needs, adjust your behavior, make yourself useful. It kept you safe. It earned you approval. It made you feel less alone. And it worked... until it didn't.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed">
                The problem is that this coping mechanism is now outdated. You don't have to continue living with a survival strategy built for a childhood you no longer live in.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed">
                Your nervous system is still running an old program designed to keep you safe in an environment that no longer exists.
              </p>

              <div className="bg-sage-50 p-8 rounded-xl border border-sage-200 my-10">
                <p className="text-xl text-gray-800 leading-relaxed mb-0">
                  <strong className="text-sage-700">The good news? It can be updated.</strong> You can teach your body that it's safe to have needs, safe to take up space, safe to say no.
                </p>
              </div>

              <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">
                Understanding Your Adaptive Response
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed">
                People-pleasing isn't a personal failing or a weakness... it's an intelligent adaptation. Your nervous system developed this response to navigate environments where your authentic self wasn't fully safe or welcomed. This mechanism helped you:
              </p>

              <ul className="space-y-4 my-8">
                <li className="flex items-start gap-3">
                  <span className="text-sage-600 mt-1">•</span>
                  <span className="text-gray-700">Maintain connection with caregivers or important relationships</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-sage-600 mt-1">•</span>
                  <span className="text-gray-700">Avoid conflict or emotional dysregulation in others</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-sage-600 mt-1">•</span>
                  <span className="text-gray-700">Create a sense of control in unpredictable situations</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-sage-600 mt-1">•</span>
                  <span className="text-gray-700">Earn approval and validation you weren't receiving consistently</span>
                </li>
              </ul>

              <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">
                When the Strategy Becomes a Struggle
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed">
                As brilliant as this coping mechanism was, it often becomes exhausting in adulthood. You might notice:
              </p>

              <ul className="space-y-4 my-8">
                <li className="flex items-start gap-3">
                  <span className="text-warm-600 mt-1">•</span>
                  <span className="text-gray-700">Chronic fatigue from constantly managing others' emotions</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-warm-600 mt-1">•</span>
                  <span className="text-gray-700">Resentment building as your own needs go unmet</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-warm-600 mt-1">•</span>
                  <span className="text-gray-700">Difficulty knowing what you actually want or need</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-warm-600 mt-1">•</span>
                  <span className="text-gray-700">Relationships that feel one-sided or unfulfilling</span>
                </li>
              </ul>

              <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">
                Updating Your Nervous System
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed">
                The path forward isn't about forcing yourself to stop people-pleasing through willpower alone. It's about helping your nervous system learn that you're now in a different environment—one where it's safe to:
              </p>

              <div className="bg-gradient-to-br from-sage-50 to-warm-50 p-8 rounded-2xl my-10 border border-sage-200">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-sage-600 text-xl">✓</span>
                    <span className="text-lg text-gray-700"><strong>Have needs</strong> without apologizing for them</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-sage-600 text-xl">✓</span>
                    <span className="text-lg text-gray-700"><strong>Take up space</strong> without making yourself small</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-sage-600 text-xl">✓</span>
                    <span className="text-lg text-gray-700"><strong>Say no</strong> without elaborate explanations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-sage-600 text-xl">✓</span>
                    <span className="text-lg text-gray-700"><strong>Disappoint others</strong> without abandoning yourself</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-sage-600 text-xl">✓</span>
                    <span className="text-lg text-gray-700"><strong>Be authentic</strong> even when it feels vulnerable</span>
                  </li>
                </ul>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                This work requires patience, compassion, and often, guidance. It's not about becoming someone new—it's about finally becoming yourself.
              </p>
            </div>

            {/* CTA Section */}
            <div className="mt-16 p-8 bg-gradient-to-br from-sage-50 to-warm-50 rounded-2xl border border-sage-200">
              <h3 className="text-2xl font-serif font-light text-sage-900 mb-4">
                Ready to Update Your Nervous System?
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Through evidence-based, neuroscience-informed approaches, I help you regulate your nervous system and build a felt sense of safety in your body—so authenticity can finally feel natural.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/services/individual-sessions" 
                  className="inline-flex items-center justify-center px-8 py-3 bg-sage-600 text-white rounded-lg hover:bg-sage-700 transition-colors font-medium"
                >
                  Explore Individual Sessions
                </Link>
                <Link 
                  to="/services/group-coaching" 
                  className="inline-flex items-center justify-center px-8 py-3 bg-white text-sage-700 border-2 border-sage-300 rounded-lg hover:bg-sage-50 transition-colors font-medium"
                >
                  Learn About Group Coaching
                </Link>
              </div>
            </div>

            {/* Author Bio */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-sage-100 flex items-center justify-center">
                    <span className="text-2xl text-sage-600">YP</span>
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Yasi Philipos</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Licensed MFT specializing in helping high-achieving women heal from people-pleasing patterns and childhood emotional neglect through nervous system regulation and somatic approaches.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
